import { useCallback, useRef, useState } from 'react';
import { makeStyles, mergeClasses, tokens, Button, Spinner, Text } from '@coreai/fluentui-react';
import { ArrowRightFilled, PauseFilled, PlayFilled } from '@fluentui/react-icons';
import { MOBILE_BREAKPOINT } from './constants';
import { ottoTokens } from '@/theme/ottoTokens';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

const useStyles = makeStyles({
  hero: {
    position: 'relative',
    overflow: 'hidden',
    backgroundImage: ottoTokens.landingHeroAmbient,
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingBlock: '100px',
    paddingInline: tokens.spacingHorizontalXXL,
    textAlign: 'center',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '64px',
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  headline: {
    display: 'block',
    fontSize: 'clamp(36px, 6vw, 64px)',
    lineHeight: 1.1,
    letterSpacing: '-0.025em',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    maxWidth: '900px',
    marginInline: 'auto',
    marginBlockEnd: tokens.spacingVerticalL,
  },
  // All three accented words use the same blue gradient — no italic,
  // no warm/cool variants. The shimmer animates background-position so
  // the gradient sweeps across the word. Per-word `animationDelay`
  // keeps the three from shimmering in lockstep.
  tagWord: {
    display: 'inline-block',
    paddingInline: tokens.spacingHorizontalXXS,
    backgroundImage: ottoTokens.landingPrimaryGradient,
    backgroundSize: '200% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    // Fallback for non-WebKit browsers — keeps the text the brand colour
    // instead of disappearing if `WebkitTextFillColor` is unsupported.
    color: ottoTokens.landingAccentTextCool,
    animationDuration: '5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: tokens.curveEasyEase,
    animationName: {
      '0%, 100%': { backgroundPositionX: '0%' },
      '50%': { backgroundPositionX: '100%' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
    },
  },
  tagWordWorkloads: { animationDelay: '0s' },
  tagWordStartup: { animationDelay: '0.7s' },
  tagWordEnterprise: { animationDelay: '1.4s' },
  subtext: {
    display: 'block',
    fontSize: tokens.fontSizeBase500,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
    maxWidth: '640px',
    marginInline: 'auto',
    marginBlockEnd: '40px',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      fontSize: tokens.fontSizeBase400,
    },
  },
  ctaStack: {
    display: 'flex',
    justifyContent: 'center',
    marginBlockEnd: '56px',
  },
  primaryCta: {
    backgroundImage: ottoTokens.landingPrimaryGradient,
    // Static near-black foreground so contrast holds on the bright
    // gradient in BOTH themes. White-on-cyan failed light-theme AA.
    color: ottoTokens.landingPrimaryGradientForeground,
    border: '1px solid transparent',
    fontWeight: tokens.fontWeightSemibold,
    transitionProperty: 'transform, box-shadow',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundImage: ottoTokens.landingPrimaryGradient,
      color: ottoTokens.landingPrimaryGradientForeground,
      transform: 'translateY(-1px)',
      boxShadow: tokens.shadow16,
    },
    ':hover:active': {
      backgroundImage: ottoTokens.landingPrimaryGradient,
      color: ottoTokens.landingPrimaryGradientForeground,
      transform: 'translateY(0)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  visualFrame: {
    position: 'relative',
    maxWidth: '1000px',
    marginInline: 'auto',
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: ottoTokens.landingCardBackground,
    overflow: 'hidden',
    boxShadow: tokens.shadow28,
    // Reserve the exact aspect ratio of the recorded video so there's
    // no layout shift when the media loads / fails. The video itself
    // also carries width/height attributes for the same reason.
    aspectRatio: '1280 / 686',
  },
  visualMedia: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  pauseToggle: {
    position: 'absolute',
    bottom: tokens.spacingVerticalM,
    right: tokens.spacingHorizontalM,
    minWidth: '32px',
    width: '32px',
    height: '32px',
    padding: 0,
    backgroundColor: ottoTokens.overlayBackdrop,
    color: tokens.colorNeutralForegroundOnBrand,
  },
});

interface HeroSectionProps {
  onLogin: (source?: string) => void;
  /** True while MSAL is verifying the silent session. Disables the
   *  primary CTA and shows a spinner so the user doesn't fire an
   *  interactive `loginRedirect` before silent SSO has had a chance. */
  isVerifyingAuth?: boolean;
}

/**
 * Recorded walkthrough of the workflow designer used in the hero.
 *
 * The capture spec (`portal/scripts/capture-hero.spec.ts`) writes WebM
 * (VP9) + MP4 (H.264) + a still poster. WebM is preferred for size;
 * MP4 is the Safari < 17 fallback. The poster also doubles as the
 * static fallback rendered when the user has reduce-motion set —
 * `<video>` cannot be paused-on-load by CSS alone, so we render a
 * plain `<img poster>` in that path instead.
 */
const HERO_VIDEO_WEBM = '/landing/workflow-hero.webm';
const HERO_VIDEO_MP4 = '/landing/workflow-hero.mp4';
const HERO_POSTER = '/landing/workflow-hero-poster.png';
const HERO_NATIVE_WIDTH = 1280;
const HERO_NATIVE_HEIGHT = 686;

export const HeroSection = ({ onLogin, isVerifyingAuth = false }: HeroSectionProps) => {
  const styles = useStyles();
  const reducedMotion = usePrefersReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [mediaFailed, setMediaFailed] = useState(false);

  const handleVideoError = useCallback(() => setMediaFailed(true), []);
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    // React state is the source of truth — `video.paused` is unreliable
    // in jsdom and would cause the toggle to no-op in tests.
    setIsPlaying((wasPlaying) => {
      if (wasPlaying) {
        v.pause();
        return false;
      }
      void v.play();
      return true;
    });
  }, []);

  const handleLogin = useCallback(() => onLogin('hero'), [onLogin]);

  // Inline render — no useMemo needed; the JSX is cheap and depends
  // on small primitives that change rarely.
  const showPoster = reducedMotion || mediaFailed;
  const visual = showPoster ? (
    <div className={styles.visualFrame}>
      <img
        src={HERO_POSTER}
        alt="Logic Apps Automation workflow designer canvas showing an HTTP trigger flowing into an LLM intent parser, a validation agent, a knowledge-base retrieval step, a human approval step, and an inline Python action."
        width={HERO_NATIVE_WIDTH}
        height={HERO_NATIVE_HEIGHT}
        className={styles.visualMedia}
        decoding="async"
        onError={handleVideoError}
      />
    </div>
  ) : (
    <div className={styles.visualFrame}>
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={HERO_POSTER}
        width={HERO_NATIVE_WIDTH}
        height={HERO_NATIVE_HEIGHT}
        className={styles.visualMedia}
        aria-label="Logic Apps Automation workflow designer being authored — an HTTP trigger flows into an LLM intent parser, a validation agent, a knowledge-base retrieval step, a human approval step, and an inline Python action."
        onError={handleVideoError}
      >
        <source src={HERO_VIDEO_WEBM} type="video/webm" />
        <source src={HERO_VIDEO_MP4} type="video/mp4" />
      </video>
      <Button
        appearance="primary"
        size="small"
        shape="circular"
        className={styles.pauseToggle}
        icon={isPlaying ? <PauseFilled /> : <PlayFilled />}
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause hero animation' : 'Play hero animation'}
        aria-pressed={!isPlaying}
      />
    </div>
  );

  return (
    <section className={styles.hero}>
      <div className={styles.inner}>
        <Text as="h1" className={styles.headline}>
          Automate <span className={mergeClasses(styles.tagWord, styles.tagWordWorkloads)}>workloads</span>.{' '}
          Build like a <span className={mergeClasses(styles.tagWord, styles.tagWordStartup)}>startup</span>.{' '}
          Ship like an <span className={mergeClasses(styles.tagWord, styles.tagWordEnterprise)}>enterprise</span>.
        </Text>
        <Text as="p" className={styles.subtext}>
          Build agents and workflows. Connect to 1,400+ systems. Zero infrastructure management —
          with the isolation, networking, and governance that enterprises need.
        </Text>
        <div className={styles.ctaStack}>
          <Button
            appearance="primary"
            size="large"
            icon={isVerifyingAuth ? <Spinner size="tiny" /> : <ArrowRightFilled />}
            iconPosition="after"
            onClick={handleLogin}
            disabled={isVerifyingAuth}
            aria-busy={isVerifyingAuth}
            className={styles.primaryCta}
          >
            {isVerifyingAuth ? 'Verifying access…' : 'Get started'}
          </Button>
        </div>
        {visual}
      </div>
    </section>
  );
};
